import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Text, Box } from 'rebass/styled-components';
import { useTheme } from 'styled-components';
import { RefreshCw, Zap, AlertTriangle, Pause } from 'lucide-react';

// Single source of truth for status appearance and copy
// Colors reference rombo theme tokens where available
const STATUS_CONFIG = {
  not_completed: {
    label: 'Not connected',
    bgColor: 'neutral.20',
  },
  not_shared: {
    label: 'Not connected',
    bgColor: 'neutral.20',
  },
  syncing: {
    label: 'Syncing',
    bgColor: 'blue.50',
    iconColor: 'blue.600',
    Icon: RefreshCw,
  },
  disconnecting: {
    label: 'Disconnecting',
    bgColor: 'neutral.20',
    iconColor: 'neutral.400',
    Icon: RefreshCw,
  },
  active: {
    label: 'Active',
    bgColor: 'green.20',
    iconColor: 'green.600',
    Icon: Zap,
  },
  in_sync: {
    label: 'Active',
    bgColor: 'green.20',
    iconColor: 'green.600',
    Icon: Zap,
  },
  permissions_missing: {
    label: 'Permissions missing',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  paused: {
    label: 'Paused',
    bgColor: 'neutral.20',
    iconColor: 'neutral.400',
    Icon: Pause,
  },
  reconnection_needed: {
    label: 'Action needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  no_recent_sync: {
    label: 'Action needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  not_logged_in_to_linkedin: {
    label: 'Action needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
};

// Diagnostic states that are only "action needed" (orange) once the sync is
// genuinely stale. Until then they render with a calm, neutral tone so the
// Team Members screen isn't alarming for members who are actually fine.
const CALM_DIAGNOSTIC_CONFIG = {
  label: 'Checking',
  bgColor: 'neutral.20',
  iconColor: 'neutral.400',
  Icon: RefreshCw,
};

export const IntegrationStatus = ({ status, tone }) => {
  const { colors } = useTheme();
  const baseConfig = STATUS_CONFIG[status] || STATUS_CONFIG.not_completed;
  const { label, bgColor, iconColor, Icon } =
    tone === 'info' && baseConfig.label === 'Action needed' ? CALM_DIAGNOSTIC_CONFIG : baseConfig;

  // Resolve icon color from theme (e.g., 'green.600' -> '#24A84E')
  const [colorKey, shade] = iconColor ? iconColor.split('.') : [];
  const resolvedIconColor = colors[colorKey]?.[shade] || iconColor;

  return (
    <Flex
      as="span"
      bg={bgColor}
      color="text.dark"
      sx={{
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        px: '12px',
        py: '0px',
        borderRadius: '99px',
      }}
    >
      {Icon && (
        <Box
          as={Icon}
          width="16px"
          height="16px"
          color={resolvedIconColor}
          aria-hidden="true"
          sx={
            status === 'syncing' || status === 'disconnecting'
              ? {
                  '@keyframes spinCcw': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                  animation: 'spinCcw 3s linear infinite',
                }
              : undefined
          }
        />
      )}
      <Text as="span" fontSize="14px" fontWeight="500" sx={{ lineHeight: '24px' }}>
        {label}
      </Text>
    </Flex>
  );
};

IntegrationStatus.propTypes = {
  status: PropTypes.oneOf([
    'syncing',
    'disconnecting',
    'in_sync',
    'active',
    'permissions_missing',
    'paused',
    'reconnection_needed',
    'no_recent_sync',
    'not_logged_in_to_linkedin',
    'not_completed',
    'not_shared',
  ]).isRequired,
  tone: PropTypes.oneOf(['warning', 'info']),
};

IntegrationStatus.defaultProps = {
  tone: 'warning',
};

export default IntegrationStatus;
